/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { forwardRef, useState } from 'react';

import { Box, Label } from '..';

import type { KeyboardEvent } from 'react';
import type { ThemeUIComponentProps } from '..';

export type DateProps = ThemeUIComponentProps<
  'forms',
  'input',
  { text: string; textColor?: string; required?: boolean }
>;

export const Date = forwardRef(
  (
    {
      text,
      textColor = 'text',
      required = false,
      variant = 'input',
      ...props
    }: DateProps,
    ref: DateProps['ref'],
  ) => {
    const [updated, setUpdated] = useState(false);

    // Set max date to limit year to 4 digits (9999-12-31)
    const maxDate = '9999-12-31';

    return (
      <Label
        data-element="Label"
        my={2}
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          color: textColor,
        }}
      >
        <Box
          as="input"
          data-element="Date"
          data-updated={updated}
          max={maxDate}
          onInput={(e) => {
            const input = e.currentTarget;
            const val = input.value;

            // basic YYYY-MM-DD check
            const match = val.match(/^(\d{4})(\d*)(-\d{2}-\d{2})?$/);
            if (match && match[2].length > 0) {
              // trim extra year digits
              input.value = match[1] + (match[3] || '');
            }
          }}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.currentTarget.value !== null) {
              setUpdated(true);
            } else {
              setUpdated(false);
            }
          }}
          ref={ref}
          type="date"
          variant={variant}
          {...props}
          __css={{
            'display': 'block',
            'width': '100%',
            'p': 2,
            'appearance': 'none',
            'fontSize': 'inherit',
            'lineHeight': 'inherit',
            'fontFamily': 'body',
            'border': '1px solid',
            'borderRadius': 4,
            'color': 'inherit',
            'bg': 'transparent',
            'textTransform': 'uppercase',
            '&[data-updated="false"]': {
              color: '#757575',
              borderColor: '#fff',
            },
            '::-webkit-calendar-picker-indicator': { display: 'none' },
            '&::-webkit-datetime-edit-year-field': {
              color: textColor,
            },
            '&::-webkit-datetime-edit-month-field': {
              color: textColor,
            },
            '&::-webkit-datetime-edit-day-field': {
              color: textColor,
            },
            '&::-webkit-datetime-edit': {
              color: textColor,
            },
            '&:not(:placeholder-shown)': {
              color: textColor,
            },
          }}
          __themeKey="forms"
        />
        {text}
        {required && '*'}
      </Label>
    );
  },
);

Date.displayName = 'Date';

export default Date;
